/**
 * Footer Component
 */

import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiGithub, FiLinkedin, FiMail, FiTwitter } from 'react-icons/fi';
import axios from 'axios';
import API_URL from '../../config/api';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const [versionInfo, setVersionInfo] = useState({
    version: '...',
    dbConnected: false
  });

  useEffect(() => {
    const checkHealth = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/health`);
        setVersionInfo({
          version: response.data.version || '1.0.0',
          dbConnected: response.data.database?.connected || false
        });
      } catch (error) {
        // Health check failure is handled gracefully (shows default version)
        // In production, this could be logged to an error tracking service
        setVersionInfo(prev => ({
          ...prev,
          dbConnected: false
        }));
      }
    };

    // Check immediately
    checkHealth();
    
    // Check every 30 seconds
    const interval = setInterval(checkHealth, 30000);
    
    return () => clearInterval(interval);
  }, []);

  return (
    <FooterContainer>
      <Container>
        <FooterContent>
          <FooterSection>
            <Logo>Richard Plotka</Logo>
            <Description>
              Professor, Technology Leader, and Innovator dedicated to advancing 
              education and leveraging technology for business transformation.
            </Description>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Quick Links</SectionTitle>
            <LinkList>
              <FooterLink href="/">Home</FooterLink>
              <FooterLink href="/about">About</FooterLink>
              <FooterLink href="/projects">Projects</FooterLink>
              <FooterLink href="/contact">Contact</FooterLink>
            </LinkList>
          </FooterSection>

          <FooterSection>
            <SectionTitle>Connect</SectionTitle>
            <SocialLinks>
              <SocialLink href="https://github.com/rplotka" target="_blank" rel="noopener noreferrer">
                <FiGithub size={20} />
              </SocialLink>
              <SocialLink href="https://linkedin.com/in/richardplotka" target="_blank" rel="noopener noreferrer">
                <FiLinkedin size={20} />
              </SocialLink>
              <SocialLink href="https://twitter.com/richardplotka" target="_blank" rel="noopener noreferrer">
                <FiTwitter size={20} />
              </SocialLink>
              <SocialLink href="mailto:plotka@rpi.edu">
                <FiMail size={20} />
              </SocialLink>
            </SocialLinks>
          </FooterSection>
        </FooterContent>

        <FooterBottom>
          <Copyright>© {currentYear} Richard Plotka. All rights reserved.</Copyright>
          <VersionInfo>
            <VersionBadge $connected={versionInfo.dbConnected}>
              <StatusDot $connected={versionInfo.dbConnected} />
              v{versionInfo.version}
            </VersionBadge>
          </VersionInfo>
          <Credits>Professor at RPI | Technology Innovator</Credits>
        </FooterBottom>
      </Container>
    </FooterContainer>
  );
};

const FooterContainer = styled.footer`
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
  padding: 3rem 0 1.5rem;
  margin-top: auto;
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const FooterContent = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  gap: 2rem;
  margin-bottom: 2rem;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    text-align: center;
  }
`;

const FooterSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const Logo = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  font-family: 'Space Grotesk', sans-serif;
  background: var(--accent-gradient);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const Description = styled.p`
  color: var(--text-secondary);
  line-height: 1.6;
`;

const SectionTitle = styled.h4`
  font-size: 1.125rem;
  margin-bottom: 0.5rem;
`;

const LinkList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FooterLink = styled.a`
  color: var(--text-secondary);
  transition: color 0.3s ease;

  &:hover {
    color: var(--accent-primary);
  }
`;

const SocialLinks = styled.div`
  display: flex;
  gap: 1rem;

  @media (max-width: 768px) {
    justify-content: center;
  }
`;

const SocialLink = styled.a`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: var(--bg-tertiary);
  color: var(--text-primary);
  transition: all 0.3s ease;

  &:hover {
    background: var(--accent-gradient);
    color: white;
    transform: translateY(-3px);
  }
`;

const FooterBottom = styled.div`
  padding-top: 2rem;
  border-top: 1px solid var(--border-color);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 1rem;
  color: var(--text-secondary);
  font-size: 0.875rem;
  flex-wrap: wrap;
  gap: 1rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.75rem;
    text-align: center;
  }
`;

const Copyright = styled.p`
  margin: 0;
  white-space: nowrap;
`;

const Credits = styled.p`
  margin: 0;
  white-space: nowrap;
`;

const VersionInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex-shrink: 0;
  min-width: fit-content;
`;

const VersionBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.25rem 0.75rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 600;
  white-space: nowrap;
  flex-shrink: 0;
  min-width: fit-content;
  background: ${props => props.$connected ? 'rgba(34, 197, 94, 0.1)' : 'rgba(239, 68, 68, 0.1)'};
  color: ${props => props.$connected ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'};
  border: 1px solid ${props => props.$connected ? 'rgba(34, 197, 94, 0.3)' : 'rgba(239, 68, 68, 0.3)'};
  transition: all 0.3s ease;
`;

const StatusDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: ${props => props.$connected ? 'rgb(34, 197, 94)' : 'rgb(239, 68, 68)'};
  animation: ${props => props.$connected ? 'pulse 2s infinite' : 'none'};

  @keyframes pulse {
    0%, 100% {
      opacity: 1;
    }
    50% {
      opacity: 0.5;
    }
  }
`;

// Memoize Footer to prevent unnecessary re-renders
export default React.memo(Footer);
